import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DocumentationService, DocFile } from '../../services/documentation/documentation.service';

@Component({
  selector: 'app-documentation',
  templateUrl: './documentation.component.html',
  styleUrls: ['./documentation.component.css']
})
export class DocumentationComponent implements OnInit {

  documentationFiles: DocFile[] = [];
  selectedFile: DocFile | null = null;
  selectedCategory: string = 'all';
  categories: string[] = [];
  loading: boolean = false;
  markdownContent: string = '';
  htmlContent: SafeHtml = '';

  constructor(
    private documentationService: DocumentationService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.documentationFiles = this.documentationService.getDocumentationFiles();
    this.categories = this.documentationService.getCategories();
  }

  selectFile(file: DocFile): void {
    this.selectedFile = file;
    this.loading = true;

    if (file.rawUrl) {
      this.documentationService.getDocumentationContent(file.rawUrl).subscribe({
        next: (content) => {
          this.markdownContent = content;
          this.htmlContent = this.markdownToHtml(content);
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading documentation:', error);
          const errorContent = '# Error al cargar la documentación\n\nNo se pudo cargar el contenido del archivo. Por favor, verifica tu conexión a internet o intenta nuevamente.';
          this.markdownContent = errorContent;
          this.htmlContent = this.markdownToHtml(errorContent);
          this.loading = false;
        }
      });
    } else {
      const noContent = '# Archivo no disponible\n\nEl contenido de este archivo no está disponible en este momento.';
      this.markdownContent = noContent;
      this.htmlContent = this.markdownToHtml(noContent);
      this.loading = false;
    }
  }

  private markdownToHtml(markdown: string): SafeHtml {
    if (!markdown) return '';

    let html = markdown;

    // Headers
    html = html.replace(/^### (.*$)/gim, '<h3 class="markdown-h3">$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2 class="markdown-h2">$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1 class="markdown-h1">$1</h1>');

    // Bold and italic
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');

    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code class="markdown-inline-code">$1</code>');

    // Code blocks
    html = html.replace(/```([\s\S]*?)```/g, '<pre class="markdown-code-block"><code>$1</code></pre>');

    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" class="markdown-link">$1</a>');

    // Lists
    html = html.replace(/^\* (.*$)/gim, '<li class="markdown-list-item">$1</li>');
    html = html.replace(/^\d+\. (.*$)/gim, '<li class="markdown-list-item">$1</li>');

    // Wrap consecutive list items
    html = html.replace(/(<li class="markdown-list-item">.*<\/li>\s*)+/g, '<ul class="markdown-list">$&</ul>');

    // Line breaks
    html = html.replace(/\n\n/g, '</p><p>');
    html = html.replace(/\n/g, '<br>');

    // Wrap in paragraph tags
    if (!html.startsWith('<')) {
      html = '<p>' + html + '</p>';
    }

    // Clean up empty paragraphs
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p><br><\/p>/g, '');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  getFilteredFiles(): DocFile[] {
    return this.documentationService.getFilteredFiles(this.documentationFiles, this.selectedCategory);
  }

  getFilesByCategory(category: string): DocFile[] {
    return this.documentationService.getFilesByCategory(this.documentationFiles, category);
  }

  setCategory(category: string): void {
    this.selectedCategory = category;
    this.selectedFile = null;
    this.markdownContent = '';
  }

  getGitHubUrl(file: DocFile): string {
    return this.documentationService.getGitHubUrl(file);
  }
}
